'use client'

import { useFloating, autoUpdate, offset, flip, shift, size, type Placement } from '@floating-ui/react-dom'

// ============================================================================
// ПАНЕЛЬ У ПОЛЯ — ОДНО ПОЗИЦИОНИРОВАНИЕ ДЛЯ ВСЕХ ВЫПАДАЮЩИХ.
// ----------------------------------------------------------------------------
// Список выбора и подсказки рисуются порталом в body: внутри окна или
// карточки с overflow-hidden панель иначе обрезалась бы. Положение считает
// floating-ui:
//   - снизу, а если внизу места нет — сверху (flip);
//   - не вылезает за край экрана (shift);
//   - высота — сколько есть места (--panel-max-h), ширина — не уже поля;
//     вся панель вместе с заголовками групп и подписью внизу не выше и не шире
//     свободного места: прежде место отдавали только списку, и подпись
//     «Показаны первые N из M» выталкивала панель за край экрана (PtQZSr1w);
//   - следит за прокруткой и изменением размеров (autoUpdate), так что панель
//     едет вместе с полем, а не висит там, где её открыли.
// ============================================================================

export function useAnchoredPanel(
  open: boolean,
  opts?: { matchWidth?: boolean; maxHeight?: number; placement?: Placement },
) {
  const matchWidth = opts?.matchWidth ?? true
  const cap = opts?.maxHeight ?? 320
  return useFloating({
    open,
    // Menus that end at their button's right edge (row «⋮») pass 'bottom-end'.
    placement: opts?.placement ?? 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: panelMiddleware({ cap, matchWidth }),
  })
}

/**
 * Цепочка положения панели. Отдельно от хука, чтобы тест прогнал её через
 * настоящий computePosition: ширина на телефоне — это ширина экрана без полей,
 * а не место справа от поля (v0gVmJtQ).
 */
export function panelMiddleware({ cap, matchWidth }: { cap: number; matchWidth: boolean }) {
  return [
    offset(4),
    flip({ padding: 8 }),
    shift({ padding: 8 }),
    size({
      padding: 8,
      apply({ availableWidth, availableHeight, rects, elements }) {
        fitPanel(elements.floating as HTMLElement, { availableWidth, availableHeight, referenceWidth: rects.reference.width, cap, matchWidth })
      },
    }),
  ]
}

/**
 * Размер панели по свободному месту. Панель — колонка (panelClass): прокручиваемый
 * список сжимается, а заголовок группы, поле поиска и подпись внизу остаются видны.
 */
export function fitPanel(
  el: HTMLElement,
  { availableWidth, availableHeight, referenceWidth, cap, matchWidth }:
    { availableWidth: number; availableHeight: number; referenceWidth: number; cap: number; matchWidth: boolean },
) {
  const height = Math.max(120, Math.min(cap, availableHeight))
  el.style.setProperty('--panel-max-h', `${height}px`)
  el.style.maxHeight = `${height}px`
  el.style.maxWidth = `${Math.max(160, availableWidth)}px`
  if (matchWidth) el.style.minWidth = `${Math.min(referenceWidth, Math.max(160, availableWidth))}px`
}

/**
 * Оформление панели — одно для списка и подсказок.
 * Слой 90 — как у меню действий: выше любого окна (70–79), ниже оповещений (100).
 */
export const panelClass =
  'z-[90] flex flex-col rounded-[10px] border-hairline border-ink-200 bg-white shadow-lg overflow-hidden'

/**
 * Метка панели. Панель живёт в портале, вне разметки своего поповера или
 * окна, и чужие «клик снаружи» сочли бы выбор пункта кликом мимо — например,
 * закрыли бы панель фильтров. useClickOutside такие клики пропускает.
 */
export const PANEL_ATTR = 'data-floating-panel'
